import { useQuery } from '@tanstack/react-query'
import {
  ALL_INTEGRATION_TYPES,
  ConnectIntegrationRequest,
  CrmIntegrationCredentialDTO,
  CrmIntegrationListItemDTO,
  DisconnectIntegrationRequest,
  INTEGRATION_METADATA,
  IntegrationDTO,
  IntegrationStatus,
  IntegrationType,
  StoreCrmCredentialsBody,
  ToggleCrmEnabledBody,
  ToggleIntegrationStatusRequest
} from 'models/integration'

import { CrmIntegrationEndpoint } from 'constants/API/endpoints'
import { EntityKeys, QueryType, getMutation } from 'utils/reactQuery'
import { useAPI } from 'utils/API/useApi'

// ============================================================================
// QUERIES
// ============================================================================

/**
 * Fetches CRM integrations and enriches BE response with FE metadata.
 *
 * BE returns minimal `{ id, crmType, enabled }` objects only for connected integrations.
 * This hook merges all known integration types with BE data to produce the full
 * `IntegrationDTO[]` the UI needs (including not-connected ones).
 *
 * @param propstackLinked whether the workspace was silently Propstack-provisioned
 * (`currentUserWorkspaceMembership.propstackLinked`, via `useWorkspaceRoles().isPropstackLinked`).
 */
export function useGetIntegrations(workspaceId: string, propstackLinked: boolean) {
  const api = useAPI<CrmIntegrationEndpoint>()

  return useQuery({
    queryKey: [EntityKeys.CRM_INTEGRATION, QueryType.LIST, { workspaceId }],
    queryFn: () => api.get<CrmIntegrationListItemDTO[]>(
      CrmIntegrationEndpoint.LIST,
      { workspaceId },
      false
    ),
    enabled: !!workspaceId,
    select: (beList): IntegrationDTO[] =>
      ALL_INTEGRATION_TYPES.map((type) => {
        const beItem = beList.find((item) => item.crmType === type)
        const metadata = INTEGRATION_METADATA[type]

        let status: IntegrationStatus
        if (!beItem) {
          status = IntegrationStatus.NOT_CONNECTED
        } else if (beItem.enabled) {
          status = IntegrationStatus.CONNECTED
        } else {
          status = IntegrationStatus.INACTIVE
        }

        // TODO(ISSUE-2426): silent Propstack provisioning creates no crm-integrations record,
        // so the BE never lists PROPSTACK for auto-provisioned workspaces. Force Connected until
        // the BE derives the status from Workspace.propstackShopId, then remove this override.
        const isSyntheticBinding = type === IntegrationType.PROPSTACK && !beItem && propstackLinked
        if (isSyntheticBinding) {
          status = IntegrationStatus.CONNECTED
        }

        return {
          id: beItem?.id ?? type,
          type,
          name: metadata.name,
          description: metadata.description,
          logoUrl: metadata.logoUrl,
          status,
          isSyntheticBinding,
        }
      })
  })
}

// ============================================================================
// MUTATIONS
// ============================================================================

/**
 * Connects an integration by storing API key credentials
 */
export function useConnectIntegration() {
  const api = useAPI<CrmIntegrationEndpoint>()

  return getMutation<CrmIntegrationCredentialDTO, ConnectIntegrationRequest>(
    ({ type, apiKey, workspaceId }) => {
      const body: StoreCrmCredentialsBody = {
        authSchemeType: 'API_KEY_HEADER',
        credential: apiKey,
      }

      return api.post<CrmIntegrationCredentialDTO>(
        CrmIntegrationEndpoint.STORE_CREDENTIALS,
        { workspaceId, crmType: type },
        body,
        false
      )
    },
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.CRM_INTEGRATION] })
    }
  )
}

/**
 * Disconnects an integration by deleting its credentials
 */
export function useDisconnectIntegration() {
  const api = useAPI<CrmIntegrationEndpoint>()

  return getMutation<void, DisconnectIntegrationRequest>(
    ({ crmType, workspaceId }) => api.delete(
      CrmIntegrationEndpoint.DELETE_CREDENTIALS,
      { workspaceId, crmType },
      false
    ),
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.CRM_INTEGRATION] })
    }
  )
}

/**
 * Toggles integration enabled/disabled status
 */
export function useToggleIntegrationStatus() {
  const api = useAPI<CrmIntegrationEndpoint>()

  return getMutation<CrmIntegrationCredentialDTO, ToggleIntegrationStatusRequest>(
    ({ crmType, enabled, workspaceId }) => {
      const body: ToggleCrmEnabledBody = { enabled }

      return api.patch<CrmIntegrationCredentialDTO>(
        CrmIntegrationEndpoint.TOGGLE_ENABLED,
        { workspaceId, crmType },
        body,
        false
      )
    },
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.CRM_INTEGRATION] })
    }
  )
}
